import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { AlertTriangle, ChevronDown, Download, LayoutGrid, List, MapPin, RefreshCw, Sparkles } from 'lucide-react';
import { colors, spacing, typography, borderRadius, breakpoints } from '../../styles/theme';
import type { Event } from '../../types/api';
import config from '../../config';
import { useAnalytics } from '../../hooks/useAnalytics';
import EventCard from '../EventCard';
import EventListRow from './EventListRow';
import EventFilterBar from './EventFilterBar';
import EventLocationMap from './EventLocationMap';
import {
  getRecentlyAddedEvents,
  groupEventsByDuration,
  groupEventsByRecency,
  isMappable,
  isUpcomingOrCurrent,
} from '../../utils/eventPresentation';
import {
  applyEventFilters,
  collectVibeOptions,
  countActiveFilters,
  parseFiltersFromParams,
  writeFiltersToParams,
  type EventFilterState,
} from '../../utils/eventFilters';
import { downloadEventExport, type ExportFormat } from '../../utils/eventExport';

/**
 * Shared shell for every "list of events for one thing" page (an entity, a
 * location). Owns view mode, URL-backed filters, the map, grouping, and
 * exports so those behaviours can never drift between surfaces.
 */

const Page = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: ${spacing[6]} ${spacing[4]} ${spacing[16]};

  @media (max-width: ${breakpoints.md}) {
    padding: ${spacing[4]} ${spacing[3]} ${spacing[12]};
  }
`;

const HeaderUtilityBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[2]} ${spacing[4]};
  min-width: 0;
  margin-bottom: ${spacing[3]};

  nav {
    flex: 1 1 240px;
    min-width: 0;
    margin-bottom: 0;
  }

  @media (max-width: ${breakpoints.sm}) {
    nav {
      flex-basis: 100%;
    }
  }
`;

const HeaderBlock = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${spacing[4]};
  margin-bottom: ${spacing[5]};
`;

const HeaderText = styled.div`
  flex: 1 1 320px;
  min-width: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  min-width: 0;
`;

const Title = styled.h1`
  margin: 0;
  font-size: ${typography.fontSizes['3xl']};
  color: ${colors.text.primary};
  overflow-wrap: anywhere;

  @media (max-width: ${breakpoints.md}) {
    font-size: ${typography.fontSizes['2xl']};
  }
`;

const Subtitle = styled.p`
  margin: ${spacing[1]} 0 0;
  font-size: ${typography.fontSizes.base};
  color: ${colors.text.secondary};
`;

const HeaderControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[2]};
  min-width: 0;
`;

const ControlButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  min-height: 44px;
  padding: 0 ${spacing[3]};
  border: 1px solid ${(props) => (props.isActive ? colors.primary[400] : colors.border.normal)};
  border-radius: ${borderRadius.md};
  background: ${(props) => (props.isActive ? colors.primary[50] : colors.white)};
  color: ${(props) => (props.isActive ? colors.primary[800] : colors.text.secondary)};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${colors.primary[300]};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${colors.border.focus};
    outline-offset: 2px;
  }
`;

const HeaderActionButton = styled(ControlButton)`
  gap: ${spacing[1]};
  min-height: 36px;
  padding: 0 ${spacing[2]};
  font-size: ${typography.fontSizes.xs};
  white-space: nowrap;

  svg {
    width: 14px;
    height: 14px;
  }

  @media (max-width: ${breakpoints.sm}) {
    min-height: 44px;
  }
`;

const MapControlButton = styled(ControlButton)`
  border-color: ${colors.primary[200]};
  background: ${colors.white};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    background: ${colors.primary[50]};
    border-color: ${colors.primary[400]};
  }
`;

const MapControlIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  border: 1px solid ${colors.secondary[200]};
  border-radius: ${borderRadius.full};
  background: ${colors.secondary[100]};
  color: ${colors.secondary[700]};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ButtonGroup = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: ${spacing[1]};
  min-width: 0;
`;

const HeaderActions = styled(HeaderControls)`
  flex: 0 0 auto;
  gap: ${spacing[1]};
  margin-left: auto;

  @media (max-width: ${breakpoints.sm}) {
    width: 100%;
    margin-left: 0;
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[4]};
`;

const ResultSummary = styled.span`
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.secondary};
`;

const EventTabs = styled.div`
  display: flex;
  gap: ${spacing[1]};
  margin: ${spacing[1]} 0 ${spacing[5]};
  border-bottom: 1px solid ${colors.border.normal};
  overflow: hidden;

  @media (max-width: ${breakpoints.sm}) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
    overflow: visible;
  }
`;

const EventTabButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  min-width: max-content;
  min-height: 48px;
  margin-bottom: -1px;
  padding: 0 ${spacing[4]};
  border: 0;
  border-bottom: 3px solid ${(props) => (props.isActive ? colors.primary[600] : 'transparent')};
  background: ${(props) => (props.isActive ? colors.primary[50] : 'transparent')};
  color: ${(props) => (props.isActive ? colors.primary[800] : colors.text.secondary)};
  font-size: ${typography.fontSizes.base};
  font-weight: ${typography.fontWeights.semibold};
  letter-spacing: 0;
  cursor: pointer;

  &:hover {
    color: ${colors.primary[800]};
    background: ${colors.primary[50]};
  }

  &:focus-visible {
    outline: 2px solid ${colors.border.focus};
    outline-offset: -2px;
  }

  @media (max-width: ${breakpoints.sm}) {
    flex-direction: column;
    gap: ${spacing[1]};
    width: 100%;
    min-width: 0;
    min-height: 64px;
    padding: ${spacing[2]};
    font-size: ${typography.fontSizes.sm};
    line-height: ${typography.lineHeights.tight};
  }
`;

const EventTabLabel = styled.span`
  white-space: nowrap;
`;

const EventTabCount = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>`
  min-width: 24px;
  padding: 2px ${spacing[2]};
  border-radius: ${borderRadius.full};
  background: ${(props) => (props.isActive ? colors.primary[600] : colors.gray[200])};
  color: ${(props) => (props.isActive ? colors.white : colors.text.secondary)};
  font-size: ${typography.fontSizes.xs};
  line-height: 1.5;
  text-align: center;

  @media (max-width: ${breakpoints.sm}) {
    box-sizing: border-box;
    min-width: 28px;
    padding: 1px ${spacing[2]};
  }
`;

const PrimaryResults = styled.div``;

const Section = styled.section`
  margin-bottom: ${spacing[6]};
`;

const SectionHeading = styled.h2`
  font-size: ${typography.fontSizes.lg};
  color: ${colors.text.primary};
  margin: 0 0 ${spacing[3]};
`;

const RecentlyAddedSection = styled(Section)`
  margin-right: -${spacing[4]};
  margin-left: -${spacing[4]};
  padding: ${spacing[4]};
  border-top: 1px solid ${colors.secondary[200]};
  border-bottom: 1px solid ${colors.secondary[200]};
  background: ${colors.secondary[50]};

  @media (max-width: ${breakpoints.md}) {
    margin-right: -${spacing[3]};
    margin-left: -${spacing[3]};
    padding: ${spacing[4]} ${spacing[3]};
  }
`;

const RecentlyAddedHeading = styled(SectionHeading)`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  color: ${colors.secondary[800]};
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${spacing[4]};

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const Disclosure = styled.details`
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  background: ${colors.background.secondary};
  padding: ${spacing[3]};

  &[open] summary svg {
    transform: rotate(180deg);
  }
`;

const DisclosureSummary = styled.summary`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  min-height: 44px;
  cursor: pointer;
  font-size: ${typography.fontSizes.base};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }

  &:focus-visible {
    outline: 2px solid ${colors.border.focus};
    outline-offset: 2px;
  }

  svg {
    transition: transform 0.15s ease;
  }
`;

const DisclosureBody = styled.div`
  margin-top: ${spacing[3]};
`;

const MapPanel = styled.div`
  margin-bottom: ${spacing[5]};
`;

const Notice = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: 'warning' | 'error' }>`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${spacing[2]};
  padding: ${spacing[3]};
  margin-bottom: ${spacing[4]};
  border-radius: ${borderRadius.md};
  border: 1px solid ${(props) => (props.tone === 'error' ? colors.error[200] : colors.warning[300])};
  background: ${(props) => (props.tone === 'error' ? colors.error[50] : colors.warning[50])};
  color: ${(props) => (props.tone === 'error' ? colors.error[800] : colors.warning[800])};
  font-size: ${typography.fontSizes.sm};

  svg {
    flex-shrink: 0;
  }
`;

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  min-height: 44px;
  margin-left: auto;
  padding: 0 ${spacing[3]};
  border: 1px solid currentColor;
  border-radius: ${borderRadius.md};
  background: transparent;
  color: inherit;
  font-size: ${typography.fontSizes.sm};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.border.focus};
    outline-offset: 2px;
  }
`;

const Skeleton = styled.div`
  height: 116px;
  border-radius: ${borderRadius.lg};
  background: linear-gradient(90deg, ${colors.gray[100]} 25%, ${colors.gray[200]} 37%, ${colors.gray[100]} 63%);
  background-size: 400% 100%;
  animation: shimmer 1.4s ease infinite;

  @keyframes shimmer {
    0% { background-position: 100% 50%; }
    100% { background-position: 0 50%; }
  }
`;

const EmptyMessage = styled.p`
  padding: ${spacing[6]};
  text-align: center;
  color: ${colors.text.secondary};
  background: ${colors.background.secondary};
  border: 1px dashed ${colors.border.normal};
  border-radius: ${borderRadius.lg};
`;

const LoadMoreRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${spacing[4]};
`;

const VIEW_PARAM = 'view';
const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

const EXPORT_BUTTONS: Array<{ format: ExportFormat; label: string }> = [
  { format: 'csv', label: 'CSV' },
  { format: 'markdown', label: 'Markdown' },
  { format: 'text', label: 'Text' },
];

/** Desktop expands the map by default; mobile collapses it. */
export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && Boolean(window.matchMedia?.(DESKTOP_MEDIA_QUERY).matches),
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    setIsDesktop(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return isDesktop;
}

export interface EventsExplorerProps {
  title: string;
  titleIcon?: React.ReactNode;
  subtitle?: React.ReactNode;
  breadcrumbs?: React.ReactNode;
  events: Event[];
  isLoading: boolean;
  /** Non-null renders a retryable inline error instead of the list. */
  error?: string | null;
  onRetry?: () => void;
  /** Server-reported total, used to warn when the fetch was truncated. */
  totalAvailable?: number;
  fetchLimit?: number;
  /** Used for export filenames and telemetry, e.g. "Elastic" or "New York". */
  exportLabel: string;
  /** Entity whose page this is, so its own relationship badge stays bare. */
  contextEntityId?: number | null;
  /** Extra controls rendered next to the filter bar (e.g. a radius selector). */
  toolbarExtra?: React.ReactNode;
  emptyMessage?: string;
  telemetryCategory?: string;
  /** Pull events added in the previous seven days into a distinct first section. */
  showRecentlyAdded?: boolean;
  /** Separate short and long-running current events into tabs. */
  showDurationTabs?: boolean;
  /** Semantic search keeps its relevance-ranked result order instead of regrouping by event date. */
  eventGrouping?: 'recency' | 'all';
  primarySectionLabel?: string;
  remoteFiltering?: boolean;
  resultTotal?: number;
  upcomingTotal?: number;
  recentTotal?: number;
  recentSectionLabel?: string;
  vibeOptionsOverride?: string[];
  entityFilterOptions?: Array<{ value: number; label: string }>;
  mapEvents?: Event[];
  hasMoreUpcoming?: boolean;
  hasMoreRecent?: boolean;
  isLoadingMoreUpcoming?: boolean;
  isLoadingMoreRecent?: boolean;
  onLoadMoreUpcoming?: () => void;
  onLoadMoreRecent?: () => void;
  onExportRequest?: (format: ExportFormat) => void;
}

export const EventsExplorer: React.FC<EventsExplorerProps> = ({
  title,
  titleIcon,
  subtitle,
  breadcrumbs,
  events,
  isLoading,
  error = null,
  onRetry,
  totalAvailable = 0,
  fetchLimit,
  exportLabel,
  contextEntityId = null,
  toolbarExtra,
  emptyMessage,
  telemetryCategory = 'events_explorer',
  showRecentlyAdded = false,
  showDurationTabs = false,
  eventGrouping = 'recency',
  primarySectionLabel = 'Upcoming events',
  remoteFiltering = false,
  resultTotal,
  upcomingTotal,
  recentTotal,
  recentSectionLabel = 'Recent past events',
  vibeOptionsOverride,
  entityFilterOptions = [],
  mapEvents,
  hasMoreUpcoming = false,
  hasMoreRecent = false,
  isLoadingMoreUpcoming = false,
  isLoadingMoreRecent = false,
  onLoadMoreUpcoming,
  onLoadMoreRecent,
  onExportRequest,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { trackEvent } = useAnalytics();
  const isDesktop = useIsDesktop();
  const tabInstanceId = useId();

  const [highlightedEventId, setHighlightedEventId] = useState<number | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [isMapOpen, setIsMapOpen] = useState(isDesktop);
  const [activeEventTab, setActiveEventTab] = useState<'upcoming' | 'ongoing'>('upcoming');
  const listRef = useRef<HTMLDivElement>(null);
  const recentDisclosureRef = useRef<HTMLDetailsElement>(null);
  const upcomingTabRef = useRef<HTMLButtonElement>(null);
  const ongoingTabRef = useRef<HTMLButtonElement>(null);
  const hasUserToggledMap = useRef(false);

  const viewMode = searchParams.get(VIEW_PARAM) === 'grid' ? 'grid' : 'list';
  const filters = useMemo(() => parseFiltersFromParams(searchParams), [searchParams]);

  // A manual toggle always wins over a later breakpoint change.
  useEffect(() => {
    if (!hasUserToggledMap.current) setIsMapOpen(isDesktop);
  }, [isDesktop]);

  // Selection belongs to the current result set only.
  useEffect(() => {
    setHighlightedEventId(null);
    setSelectedEventId(null);
    if (showDurationTabs) setActiveEventTab('upcoming');
  }, [events, showDurationTabs]);

  const filteredEvents = useMemo(
    () => remoteFiltering ? events : applyEventFilters(events, filters),
    [events, filters, remoteFiltering],
  );
  const allRecentlyAdded = useMemo(
    () => showRecentlyAdded ? getRecentlyAddedEvents(filteredEvents) : [],
    [filteredEvents, showRecentlyAdded],
  );
  const allRecentlyAddedIds = useMemo(
    () => new Set(allRecentlyAdded.map((event) => event.eventId)),
    [allRecentlyAdded],
  );
  const regularEvents = useMemo(
    () => showDurationTabs
      ? filteredEvents
      : filteredEvents.filter((event) => !allRecentlyAddedIds.has(event.eventId)),
    [allRecentlyAddedIds, filteredEvents, showDurationTabs],
  );
  const { upcoming: currentEvents, recentPast: groupedRecentPast } = useMemo(
    () => eventGrouping === 'all'
      ? { upcoming: regularEvents, recentPast: [] }
      : groupEventsByRecency(regularEvents),
    [eventGrouping, regularEvents],
  );
  const archivedRecentlyAdded = useMemo(
    () => showDurationTabs
      ? allRecentlyAdded.filter((event) => !isUpcomingOrCurrent(event))
      : [],
    [allRecentlyAdded, showDurationTabs],
  );
  const archivedRecentlyAddedIds = useMemo(
    () => new Set(archivedRecentlyAdded.map((event) => event.eventId)),
    [archivedRecentlyAdded],
  );
  const recentPast = useMemo(
    () => showDurationTabs
      ? groupedRecentPast.filter((event) => !archivedRecentlyAddedIds.has(event.eventId))
      : groupedRecentPast,
    [archivedRecentlyAddedIds, groupedRecentPast, showDurationTabs],
  );
  const durationGroups = useMemo(
    () => groupEventsByDuration(currentEvents, config.eventDisplay.ongoingDurationHours),
    [currentEvents],
  );
  const durationTabsVisible = showDurationTabs && durationGroups.ongoing.length > 0;
  const activeCurrentEvents = durationTabsVisible
    ? durationGroups[activeEventTab]
    : currentEvents;

  useEffect(() => {
    if (!durationTabsVisible && activeEventTab !== 'upcoming') {
      setActiveEventTab('upcoming');
    }
  }, [activeEventTab, durationTabsVisible]);
  const recentlyAdded = useMemo(
    () => showDurationTabs && showRecentlyAdded
      ? activeCurrentEvents.filter((event) => allRecentlyAddedIds.has(event.eventId))
      : allRecentlyAdded,
    [activeCurrentEvents, allRecentlyAdded, allRecentlyAddedIds, showDurationTabs, showRecentlyAdded],
  );
  const recentlyAddedIds = useMemo(
    () => new Set(recentlyAdded.map((event) => event.eventId)),
    [recentlyAdded],
  );
  const upcoming = useMemo(
    () => showDurationTabs
      ? activeCurrentEvents.filter((event) => !recentlyAddedIds.has(event.eventId))
      : currentEvents,
    [activeCurrentEvents, currentEvents, recentlyAddedIds, showDurationTabs],
  );
  const vibeOptions = useMemo(
    () => vibeOptionsOverride || collectVibeOptions(events),
    [events, vibeOptionsOverride],
  );
  const mapCandidates = mapEvents || filteredEvents;
  const eventsForMap = useMemo(
    () => mapCandidates.filter((event) => isUpcomingOrCurrent(event)),
    [mapCandidates],
  );
  const mappableCount = useMemo(() => eventsForMap.filter(isMappable).length, [eventsForMap]);
  const hasAnyMatchingEvents = showDurationTabs
    ? currentEvents.length > 0 || recentPast.length > 0 || archivedRecentlyAdded.length > 0
    : recentlyAdded.length > 0 || upcoming.length > 0 || recentPast.length > 0;
  const hasActiveTabEvents = recentlyAdded.length > 0 || upcoming.length > 0;

  const isTruncated = !remoteFiltering && totalAvailable > events.length && events.length > 0;
  const remainingEvents = Math.max(0, (resultTotal ?? totalAvailable) - events.length);
  const nextBatchSize = fetchLimit ? Math.min(fetchLimit, remainingEvents) : 0;
  const loadMoreLabel = nextBatchSize > 0
    ? `${remainingEvents <= nextBatchSize ? 'Load remaining' : 'Load next'} ${nextBatchSize} event${nextBatchSize === 1 ? '' : 's'}`
    : 'Load more events';

  useEffect(() => {
    if (!isTruncated) return;
    console.warn(`${exportLabel}: ${totalAvailable} events available but only ${events.length} fetched.`);
    trackEvent({
      action: 'events_truncated',
      category: telemetryCategory,
      label: exportLabel,
      value: totalAvailable,
      custom_parameters: { fetched: events.length, limit: fetchLimit },
    });
  }, [isTruncated, totalAvailable, events.length, exportLabel, fetchLimit, trackEvent, telemetryCategory]);

  // Exports follow the on-screen order and include collapsed recent-past rows.
  const exportOrder = useMemo(
    () => showDurationTabs
      ? [
          ...allRecentlyAdded,
          ...currentEvents.filter((event) => !allRecentlyAddedIds.has(event.eventId)),
          ...groupedRecentPast.filter((event) => !allRecentlyAddedIds.has(event.eventId)),
        ]
      : [...recentlyAdded, ...upcoming, ...recentPast],
    [
      allRecentlyAdded,
      allRecentlyAddedIds,
      currentEvents,
      groupedRecentPast,
      recentPast,
      recentlyAdded,
      showDurationTabs,
      upcoming,
    ],
  );

  const selectEventTab = useCallback((tab: 'upcoming' | 'ongoing', moveFocus = false) => {
    if (tab !== activeEventTab) {
      setActiveEventTab(tab);
      trackEvent({
        action: 'event_duration_tab_change',
        category: telemetryCategory,
        label: tab,
        custom_parameters: {
          duration_threshold_hours: config.eventDisplay.ongoingDurationHours,
        },
      });
    }
    if (moveFocus) {
      window.requestAnimationFrame(() => {
        (tab === 'upcoming' ? upcomingTabRef : ongoingTabRef).current?.focus();
      });
    }
  }, [activeEventTab, telemetryCategory, trackEvent]);

  const handleEventTabKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
    let nextTab: 'upcoming' | 'ongoing' | null = null;
    if (event.key === 'ArrowLeft' || event.key === 'Home') nextTab = 'upcoming';
    if (event.key === 'ArrowRight' || event.key === 'End') nextTab = 'ongoing';
    if (!nextTab) return;
    event.preventDefault();
    selectEventTab(nextTab, true);
  }, [selectEventTab]);

  const updateFilters = useCallback((next: EventFilterState) => {
    const changedGroup = (Object.keys(next) as Array<keyof EventFilterState>)
      .find((key) => JSON.stringify(filters[key]) !== JSON.stringify(next[key]));
    setSearchParams(writeFiltersToParams(searchParams, next), { replace: true });
    trackEvent({
      action: 'filter_change',
      category: telemetryCategory,
      custom_parameters: {
        filter_group: changedGroup || 'unknown',
        active_filters: countActiveFilters(next),
        horizon_days: next.horizonDays,
      },
    });
  }, [filters, searchParams, setSearchParams, telemetryCategory, trackEvent]);

  const setViewMode = useCallback(
    (mode: 'list' | 'grid') => {
      const next = new URLSearchParams(searchParams);
      if (mode === 'grid') next.set(VIEW_PARAM, 'grid');
      else next.delete(VIEW_PARAM);
      setSearchParams(next, { replace: true });
      trackEvent({
        action: 'view_mode_change',
        category: telemetryCategory,
        custom_parameters: { view: mode },
      });
    },
    [searchParams, setSearchParams, telemetryCategory, trackEvent],
  );

  const handleExport = useCallback(
    (format: ExportFormat) => {
      if ((resultTotal ?? exportOrder.length) === 0) return;
      if (onExportRequest) onExportRequest(format);
      else downloadEventExport(exportOrder, format, exportLabel);
      trackEvent({
        action: 'export_events',
        category: telemetryCategory,
        label: format,
        value: resultTotal ?? exportOrder.length,
        custom_parameters: { action_location: 'explorer_toolbar', format },
      });
    },
    [exportOrder, exportLabel, onExportRequest, resultTotal, trackEvent, telemetryCategory],
  );

  const handleMarkerSelect = useCallback((eventId: number) => {
    setSelectedEventId(eventId);
    setHighlightedEventId(eventId);
    trackEvent({
      action: 'map_marker_select',
      category: telemetryCategory,
      label: String(eventId),
    });
    trackEvent({
      action: 'map_details_pane_open',
      category: telemetryCategory,
      label: String(eventId),
    });
  }, [telemetryCategory, trackEvent]);

  const handleSelectionClose = useCallback(() => {
    setSelectedEventId(null);
    setHighlightedEventId(null);
  }, []);

  const handleFindInList = useCallback((eventId: number) => {
    const isRecentPast = recentPast.some(event => event.eventId === eventId);
    if (isRecentPast && recentDisclosureRef.current) {
      recentDisclosureRef.current.open = true;
    }
    if (durationTabsVisible && !isRecentPast) {
      const targetTab = durationGroups.ongoing.some(event => event.eventId === eventId)
        ? 'ongoing'
        : 'upcoming';
      if (targetTab !== activeEventTab) setActiveEventTab(targetTab);
    }
    window.requestAnimationFrame(() => {
      const row = document.querySelector<HTMLElement>(`[data-event-id="${eventId}"]`);
      if (!row) return;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      row.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
      row.focus({ preventScroll: true });
    });
    trackEvent({
      action: 'map_find_in_list',
      category: telemetryCategory,
      label: String(eventId),
      custom_parameters: { action_location: 'map_details' },
    });
  }, [activeEventTab, durationGroups.ongoing, durationTabsVisible, recentPast, telemetryCategory, trackEvent]);

  const handleViewEvent = useCallback((eventId: number) => {
    trackEvent({
      action: 'map_view_event',
      category: telemetryCategory,
      label: String(eventId),
      custom_parameters: { action_location: 'map_details' },
    });
  }, [telemetryCategory, trackEvent]);

  const toggleMap = useCallback(() => {
    hasUserToggledMap.current = true;
    setIsMapOpen((open) => {
      trackEvent({
        action: 'map_toggle',
        category: telemetryCategory,
        custom_parameters: { outcome: open ? 'closed' : 'opened' },
      });
      return !open;
    });
  }, [telemetryCategory, trackEvent]);

  return (
    <Page>
      <HeaderUtilityBar>
        {breadcrumbs}

        <HeaderActions>
          <ButtonGroup role="group" aria-label="Export events">
            {EXPORT_BUTTONS.map(({ format, label }) => (
              <HeaderActionButton
                key={format}
                type="button"
                onClick={() => handleExport(format)}
                disabled={(resultTotal ?? exportOrder.length) === 0}
                aria-label={`Export ${label}`}
              >
                <Download aria-hidden="true" />
                {label}
              </HeaderActionButton>
            ))}
          </ButtonGroup>

          <ButtonGroup role="group" aria-label="View mode">
            <HeaderActionButton
              type="button"
              isActive={viewMode === 'list'}
              aria-pressed={viewMode === 'list'}
              onClick={() => setViewMode('list')}
            >
              <List aria-hidden="true" />
              List
            </HeaderActionButton>
            <HeaderActionButton
              type="button"
              isActive={viewMode === 'grid'}
              aria-pressed={viewMode === 'grid'}
              onClick={() => setViewMode('grid')}
            >
              <LayoutGrid aria-hidden="true" />
              Grid
            </HeaderActionButton>
          </ButtonGroup>
        </HeaderActions>
      </HeaderUtilityBar>

      <HeaderBlock>
        <HeaderText>
          <TitleRow>
            {titleIcon}
            <Title>{title}</Title>
          </TitleRow>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </HeaderText>
      </HeaderBlock>

      {error && (
        <Notice tone="error" role="alert">
          <AlertTriangle aria-hidden="true" size={18} />
          <span>{error}</span>
          {onRetry && (
            <RetryButton type="button" onClick={onRetry}>
              <RefreshCw aria-hidden="true" size={14} />
              Retry
            </RetryButton>
          )}
        </Notice>
      )}

      {isTruncated && (
        <Notice tone="warning" role="status">
          <AlertTriangle aria-hidden="true" size={18} />
          <span>
            {`Showing the first ${events.length} of ${totalAvailable} events. Narrow the filters to see the rest.`}
          </span>
        </Notice>
      )}

      {!error && (
        <>
          <Toolbar>
            <EventFilterBar
              filters={filters}
              onChange={updateFilters}
              vibeOptions={vibeOptions}
              resultSummary={
                <ResultSummary role="status" aria-live="polite">
                  {isLoading
                    ? 'Loading events…'
                      : `${filteredEvents.length} of ${resultTotal ?? events.length} event${(resultTotal ?? events.length) === 1 ? '' : 's'}`}
                </ResultSummary>
              }
              entityOptions={entityFilterOptions}
              isLoading={isLoading}
              resultCount={resultTotal ?? filteredEvents.length}
            />
            <HeaderControls>
              {toolbarExtra}
              <MapControlButton type="button" onClick={toggleMap} aria-expanded={isMapOpen} aria-controls="events-map">
                <MapControlIcon aria-hidden="true">
                  <MapPin size={14} />
                </MapControlIcon>
                {isMapOpen ? 'Hide map' : `Show map (${mappableCount})`}
              </MapControlButton>
            </HeaderControls>
          </Toolbar>

          {isMapOpen && (
            <MapPanel id="events-map">
              <EventLocationMap
                events={eventsForMap}
                highlightedEventId={highlightedEventId}
                selectedEventId={selectedEventId}
                onMarkerSelect={handleMarkerSelect}
                onSelectionClose={handleSelectionClose}
                onFindInList={handleFindInList}
                onViewEvent={handleViewEvent}
                isLoading={isLoading}
                emptyMessage={
                  filteredEvents.length === 0
                    ? 'No events match the current filters, so there is nothing to map.'
                    : 'None of these events have a mapped location yet. They are all listed below.'
                }
              />
            </MapPanel>
          )}

          {isLoading && (
            <ListContainer aria-busy="true" aria-label="Loading events">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} />
              ))}
            </ListContainer>
          )}

          {!isLoading && durationTabsVisible && (
            <EventTabs role="tablist" aria-label="Event duration">
              <EventTabButton
                ref={upcomingTabRef}
                id={`${tabInstanceId}-upcoming-tab`}
                type="button"
                role="tab"
                isActive={activeEventTab === 'upcoming'}
                aria-selected={activeEventTab === 'upcoming'}
                aria-controls={`${tabInstanceId}-events-panel`}
                tabIndex={activeEventTab === 'upcoming' ? 0 : -1}
                onClick={() => selectEventTab('upcoming')}
                onKeyDown={handleEventTabKeyDown}
              >
                <EventTabLabel>Upcoming Events</EventTabLabel>
                <EventTabCount isActive={activeEventTab === 'upcoming'}>{durationGroups.upcoming.length}</EventTabCount>
              </EventTabButton>
              <EventTabButton
                ref={ongoingTabRef}
                id={`${tabInstanceId}-ongoing-tab`}
                type="button"
                role="tab"
                isActive={activeEventTab === 'ongoing'}
                aria-selected={activeEventTab === 'ongoing'}
                aria-controls={`${tabInstanceId}-events-panel`}
                tabIndex={activeEventTab === 'ongoing' ? 0 : -1}
                onClick={() => selectEventTab('ongoing')}
                onKeyDown={handleEventTabKeyDown}
              >
                <EventTabLabel>Ongoing Events</EventTabLabel>
                <EventTabCount isActive={activeEventTab === 'ongoing'}>{durationGroups.ongoing.length}</EventTabCount>
              </EventTabButton>
            </EventTabs>
          )}

          {!isLoading && !hasAnyMatchingEvents && (
            <EmptyMessage role="status">
              {events.length === 0
                ? emptyMessage || 'There are no upcoming or recent events here yet.'
                : 'No events match the current filters. Try clearing some filters.'}
            </EmptyMessage>
          )}

          {!isLoading && hasAnyMatchingEvents && (
            <PrimaryResults
              id={durationTabsVisible ? `${tabInstanceId}-events-panel` : undefined}
              role={durationTabsVisible ? 'tabpanel' : undefined}
              aria-labelledby={durationTabsVisible ? `${tabInstanceId}-${activeEventTab}-tab` : undefined}
            >
              {durationTabsVisible && !hasActiveTabEvents && (
                <EmptyMessage role="status">
                  {`There are no ${activeEventTab} events matching the current filters.`}
                </EmptyMessage>
              )}

              {recentlyAdded.length > 0 && (
                <RecentlyAddedSection aria-label="Events added in the past 7 days">
                  <RecentlyAddedHeading>
                    <Sparkles aria-hidden="true" size={18} />
                    {`Recently added (${recentlyAdded.length})`}
                  </RecentlyAddedHeading>
                  {viewMode === 'grid' ? (
                    <GridContainer role="list">
                      {recentlyAdded.map((event) => (
                        <EventCard key={event.eventId} event={event} contextEntityId={contextEntityId} />
                      ))}
                    </GridContainer>
                  ) : (
                    <ListContainer role="list">
                      {recentlyAdded.map((event) => (
                        <EventListRow
                          key={event.eventId}
                          event={event}
                          contextEntityId={contextEntityId}
                          isHighlighted={highlightedEventId === event.eventId}
                          onHoverChange={setHighlightedEventId}
                        />
                      ))}
                    </ListContainer>
                  )}
                </RecentlyAddedSection>
              )}

              {upcoming.length > 0 && (
                <Section aria-label={durationTabsVisible ? `${activeEventTab} events` : eventGrouping === 'all' ? primarySectionLabel : 'Upcoming events'}>
                  {!durationTabsVisible && (
                    <SectionHeading>
                      {`${eventGrouping === 'all' ? primarySectionLabel : 'Upcoming events'} (${eventGrouping === 'all' ? upcoming.length : upcomingTotal ?? upcoming.length})`}
                    </SectionHeading>
                  )}
                  {viewMode === 'grid' ? (
                    <GridContainer role="list">
                      {upcoming.map((event) => (
                        <EventCard key={event.eventId} event={event} contextEntityId={contextEntityId} />
                      ))}
                    </GridContainer>
                  ) : (
                    <ListContainer ref={listRef} role="list">
                      {upcoming.map((event) => (
                        <EventListRow
                          key={event.eventId}
                          event={event}
                          contextEntityId={contextEntityId}
                          isHighlighted={highlightedEventId === event.eventId}
                          onHoverChange={setHighlightedEventId}
                        />
                      ))}
                    </ListContainer>
                  )}
                </Section>
              )}
              {hasMoreUpcoming && onLoadMoreUpcoming && (
                <LoadMoreRow>
                  <ControlButton type="button" onClick={onLoadMoreUpcoming} disabled={isLoadingMoreUpcoming}>
                    {isLoadingMoreUpcoming ? 'Loading events…' : loadMoreLabel}
                  </ControlButton>
                </LoadMoreRow>
              )}
            </PrimaryResults>
          )}

          {!isLoading && archivedRecentlyAdded.length > 0 && (
            <RecentlyAddedSection aria-label="Recently added past events">
              <RecentlyAddedHeading>
                <Sparkles aria-hidden="true" size={18} />
                {`Recently added (${archivedRecentlyAdded.length})`}
              </RecentlyAddedHeading>
              {viewMode === 'grid' ? (
                <GridContainer role="list">
                  {archivedRecentlyAdded.map((event) => (
                    <EventCard key={event.eventId} event={event} contextEntityId={contextEntityId} />
                  ))}
                </GridContainer>
              ) : (
                <ListContainer role="list">
                  {archivedRecentlyAdded.map((event) => (
                    <EventListRow
                      key={event.eventId}
                      event={event}
                      contextEntityId={contextEntityId}
                      isHighlighted={highlightedEventId === event.eventId}
                      onHoverChange={setHighlightedEventId}
                    />
                  ))}
                </ListContainer>
              )}
            </RecentlyAddedSection>
          )}

          {!isLoading && recentPast.length > 0 && (
            <Section>
              <Disclosure ref={recentDisclosureRef}>
                <DisclosureSummary>
                  <ChevronDown aria-hidden="true" size={18} />
                  {`${recentSectionLabel} (${recentTotal ?? recentPast.length})`}
                </DisclosureSummary>
                <DisclosureBody>
                  {viewMode === 'grid' ? (
                    <GridContainer role="list">
                      {recentPast.map((event) => (
                        <EventCard key={event.eventId} event={event} contextEntityId={contextEntityId} />
                      ))}
                    </GridContainer>
                  ) : (
                    <ListContainer role="list">
                      {recentPast.map((event) => (
                        <EventListRow
                          key={event.eventId}
                          event={event}
                          contextEntityId={contextEntityId}
                          isHighlighted={highlightedEventId === event.eventId}
                          isPast
                          onHoverChange={setHighlightedEventId}
                        />
                      ))}
                    </ListContainer>
                  )}
                  {hasMoreRecent && onLoadMoreRecent && (
                    <LoadMoreRow>
                      <ControlButton type="button" onClick={onLoadMoreRecent} disabled={isLoadingMoreRecent}>
                        {isLoadingMoreRecent ? 'Loading events' : 'Load more events'}
                      </ControlButton>
                    </LoadMoreRow>
                  )}
                </DisclosureBody>
              </Disclosure>
            </Section>
          )}
        </>
      )}
    </Page>
  );
};

export { ControlButton as ExplorerControlButton };
export default EventsExplorer;
